// ABOUTME: Chatwoot Website SDK loader and typed wrappers around the global `window.$chatwoot` API.
// ABOUTME: Script injection is deferred until `loadChatwootSDK()` is called the first time.

import config from "@/config"

interface ChatwootSettings {
  hideMessageBubble?: boolean
  position?: "left" | "right"
  locale?: string
  type?: "standard" | "expanded_bubble"
  launcherTitle?: string
}

interface ChatwootUser {
  name?: string
  email?: string
  avatar_url?: string
  identifier_hash: string
}

type CustomAttributeValue = string | number | boolean | null

interface ChatwootAPI {
  isOpen?: boolean
  toggle: (state?: "open" | "close") => void
  setUser: (identifier: string, user: ChatwootUser) => void
  setCustomAttributes: (attrs: Record<string, CustomAttributeValue>) => void
  setLocale: (locale: string) => void
  reset: () => void
}

declare global {
  interface Window {
    chatwootSettings?: ChatwootSettings
    chatwootSDK?: { run: (args: { websiteToken: string; baseUrl: string }) => void }
    $chatwoot?: ChatwootAPI
  }
}

export interface ChatwootConfig {
  baseUrl: string
  websiteToken: string
}

export const getChatwootConfig = (): ChatwootConfig | null => {
  const baseUrl = config.CHATWOOT_BASE_URL
  const websiteToken = config.CHATWOOT_WEBSITE_TOKEN
  if (!baseUrl || !websiteToken) return null

  return { baseUrl, websiteToken }
}

let loadPromise: Promise<void> | null = null

export const loadChatwootSDK = (config: ChatwootConfig): Promise<void> => {
  if (loadPromise) return loadPromise
  if (window.$chatwoot) return Promise.resolve()

  loadPromise = new Promise<void>((resolve, reject) => {
    window.chatwootSettings = {
      hideMessageBubble: true,
      position: "right",
      type: "standard",
    }

    const onReady = () => resolve()
    window.addEventListener("chatwoot:ready", onReady, { once: true })

    const script = document.createElement("script")
    script.src = `${config.baseUrl}/packs/js/sdk.js`
    script.async = true
    script.defer = true
    script.onload = () => {
      const sdk = window.chatwootSDK
      if (!sdk) {
        window.removeEventListener("chatwoot:ready", onReady)
        reject(new Error("Chatwoot SDK loaded but window.chatwootSDK is missing"))

        return
      }
      sdk.run({ websiteToken: config.websiteToken, baseUrl: config.baseUrl })
    }
    script.onerror = () => {
      window.removeEventListener("chatwoot:ready", onReady)
      loadPromise = null
      reject(new Error(`Failed to load Chatwoot SDK from ${config.baseUrl}`))
    }
    document.head.appendChild(script)
  })

  return loadPromise
}

export const openChatwoot = () => window.$chatwoot?.toggle("open")
export const closeChatwoot = () => window.$chatwoot?.toggle("close")
